import { ATSPrincipalAppCandidatesListTestIds } from 'data-testids/ATS';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  Typography,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { CandidateRow } from './CandidateRow';
import { CandidatesListProps } from 'features/Principal/types/candidatesListTypes';
import { CandidatesListTableHeader } from './CandidatesListTableHeader';
import auth from 'utils/auth';
import { useInfiniteQueryGetPrincipalCandidatesList } from '../api/candidatesListQueries';
import { ConnectCandidateRowSkeleton } from 'features/Connect/features/ConnectATSIntegration/CandidateList/CandidateRow/components/ConnectCandidateRowSkeleton';
import { Alert } from 'sharedComponents/Alert';
import { getAxiosErrorMessageByStatusCode } from 'hooks/errorHandling/errorHandlingHooks';
import { CandidateModal } from '../CandidateModal';
import { ReferencesModal } from '../ReferencesModal/ReferencesModal';
import { PrincipalApplicationModal } from '../constants';
import { PrincipalApplicationModalValue } from 'features/Principal/types/modalTypes';

export const CandidatesList: React.FC<CandidatesListProps> = ({ ordering }) => {
  const [currentRowId, setCurrentRowId] = useState(null);
  const [page, setPage] = useState(0);
  const [currentModal, setCurrentModal] = useState<PrincipalApplicationModalValue | null>(null);
  const theme = useTheme();
  const user = auth.getUser();
  const schoolId = user?.profile.school.id;
  const { data, isLoading, fetchNextPage, isError, error } =
    useInfiniteQueryGetPrincipalCandidatesList(schoolId, ordering);
  const pages = data?.pages ?? [];
  const pageCount = pages.length;
  const displayPage = pageCount ? Math.min(page, pageCount - 1) : 0;
  const currentPageResults = pages[displayPage]?.results ?? [];
  const errorMessage = getAxiosErrorMessageByStatusCode(error);

  const handlePagination = async (_event: unknown, newPage: number) => {
    if (newPage > page && newPage >= pageCount) {
      await fetchNextPage();
    }

    setPage(newPage);
  };

  const handleRowClick = (rowId: number) => {
    setCurrentRowId(rowId);
    setCurrentModal(PrincipalApplicationModal.CANDIDATE);
  };

  const currentRowData = pages
    .flatMap((p) => p.results)
    .find((row) => row.application.id === currentRowId);

  const handleReferenceIconClick = (rowId: number) => {
    setCurrentRowId(rowId);
    setCurrentModal(PrincipalApplicationModal.REFERENCE);
  };

  const handleCloseModal = () => {
    setCurrentRowId(null);
    return setCurrentModal(null);
  };

  if (isLoading) {
    return <ConnectCandidateRowSkeleton />;
  }

  if (!data || isError) {
    return <Alert type="error">{errorMessage ?? 'Something went wrong'}</Alert>;
  }

  if (data?.pages[0]?.count === 0) {
    return <Typography>You have no candidates at this moment</Typography>;
  }

  if (currentPageResults) {
    return (
      <>
        <Box
          sx={{
            backgroundColor: theme.palette.white.main,
            border: `1px solid ${theme.palette.border.main}`,
            borderRadius: '4px 4px 0 0',
          }}
        >
          <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
            <Table data-testid={ATSPrincipalAppCandidatesListTestIds.CANDIDATES_LIST_TABLE}>
              <CandidatesListTableHeader />
              <TableBody>
                {currentPageResults?.map((row, index) => (
                  <CandidateRow
                    row={row}
                    key={row.application.id}
                    index={index}
                    onClick={() => handleRowClick(row.application.id)}
                    onReferenceClick={() => handleReferenceIconClick(row.application.id)}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[]}
            component="div"
            count={data?.pages[0]?.count}
            rowsPerPage={10}
            page={displayPage}
            onPageChange={handlePagination}
            data-testid={ATSPrincipalAppCandidatesListTestIds.PAGINATION}
          />
        </Box>
        {currentRowData && currentModal === PrincipalApplicationModal.CANDIDATE && (
          <CandidateModal
            open={currentRowId !== null}
            onClose={handleCloseModal}
            currentRow={currentRowData}
            key={currentRowData.candidate.id}
          />
        )}
        {currentRowData && currentModal === PrincipalApplicationModal.REFERENCE && (
          <ReferencesModal
            open={currentModal === PrincipalApplicationModal.REFERENCE}
            onClose={handleCloseModal}
            roleTitle={currentRowData.role.title}
            key={currentRowData.candidate.id}
            candidate={{
              first_name: currentRowData.candidate.first_name,
              last_name: currentRowData.candidate.last_name,
              id: currentRowData.candidate.id,
            }}
          />
        )}
      </>
    );
  }

  return <></>;
};
